import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Box,
    Typography,
    CircularProgress,
    Tooltip,
    Slider,
} from '@mui/material';
import {
    Close, ZoomIn, ZoomOut, Download,
    Edit, Create, BorderColor, DeleteOutline, Undo, CloudUpload,
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { getApiUrl } from '../config';
import { isRasterImage } from '../utils/uploadUtils';
import { showAlert } from '../redux/slices/alerts';

const isSameOrigin = (value) => {
    try {
        return new URL(value, window.location.href).origin === window.location.origin;
    } catch (err) {
        return false;
    }
};

const RASTER_EXTENSIONS = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', jfif: 'image/jpeg', pjpeg: 'image/pjpeg', gif: 'image/gif', webp: 'image/webp', avif: 'image/avif', bmp: 'image/bmp', ico: 'image/x-icon' };

const mimeTypeFromUrl = (value) => {
    if (typeof value !== 'string') return null;
    if (value.startsWith('data:')) return value.slice(5).split(/[;,]/)[0] || null;
    try {
        const segment = new URL(value, window.location.href).pathname.split('/').pop() || '';
        const dot = segment.lastIndexOf('.');
        if (dot < 1) return null;
        return RASTER_EXTENSIONS[segment.slice(dot + 1).toLowerCase()] || 'application/octet-stream';
    } catch (err) {
        return null;
    }
};

const ImageContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    maxHeight: '80vh',
    overflow: 'hidden',
}));

const StyledImage = styled('img')(({ zoom }) => ({
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    transform: `scale(${zoom})`,
    transition: 'transform 0.3s ease-in-out',
    cursor: 'zoom-in',
    zIndex: 1,
}));

const EditCanvas = styled('canvas')({
    maxWidth: '100%',
    maxHeight: '60vh',
    display: 'block',
    cursor: 'crosshair',
    touchAction: 'none',
});

const ControlsContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
    zIndex: 10,
}));

const EditToolbar = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(20, 20, 20, 0.95)',
    padding: theme.spacing(1, 2),
    alignItems: 'center',
    flexWrap: 'wrap',
    borderTop: '1px solid rgba(255,255,255,0.1)',
}));

const ControlButton = styled(IconButton)({
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
});

const ToolButton = styled(IconButton)(({ active }) => ({
    color: 'white',
    backgroundColor: active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
    outline: active ? '2px solid rgba(255,255,255,0.6)' : 'none',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    minHeight: '400px',
}));

const LoadingOverlay = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
});

const ColorSwatch = styled('input')({
    width: 32,
    height: 32,
    border: '2px solid rgba(255,255,255,0.4)',
    borderRadius: 4,
    padding: 0,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    '&::-webkit-color-swatch-wrapper': { padding: 0 },
    '&::-webkit-color-swatch': { border: 'none', borderRadius: 2 },
});

const SliderLabel = styled(Typography)(({theme}) => ({
    color: 'rgba(255,255,255,0.7)',
    fontSize: theme.typography.size.xs,
    whiteSpace: 'nowrap',
}));

const DarkDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
    },
});

const ResetDialogContent = styled(DialogContent)({
    padding: 0,
    position: 'relative',
});

const ZoomLabel = styled(Typography)({
    color: 'white',
});

const CanvasWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
    marginTop: theme.spacing(5),
}));

const ToolbarDivider = styled(Box)(({ theme }) => ({
    width: 1,
    borderLeft: '1px solid rgba(255,255,255,0.2)',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    height: 28,
}));

const SizeControlGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 120,
}));

const WhiteSlider = styled(Slider)({
    color: 'white',
    width: 80,
});

const ToolbarSpacer = styled(Box)({
    flex: 1,
});

const WhiteCircularProgress = styled(CircularProgress)({
    color: 'white',
});

const ImageViewer = ({ open, onClose, attachment, onDownload, onReupload }) => {
    const dispatch = useDispatch();
    const [zoom, setZoom] = useState(1);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [currentAttachment, setCurrentAttachment] = useState(attachment);
    const [downloading, setDownloading] = useState(false);

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [tool, setTool] = useState('pen');
    const [color, setColor] = useState('#ff0000');
    const [brushSize, setBrushSize] = useState(6);
    const [isDrawing, setIsDrawing] = useState(false);
    const [uploadingEdit, setUploadingEdit] = useState(false);
    const [undoStack, setUndoStack] = useState([]);

    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const lastPos = useRef(null);
    const loadedSrcRef = useRef(null);

    const attachmentId = currentAttachment?._id || '';
    const directUrl = !attachmentId && typeof currentAttachment?.url === 'string' ? currentAttachment.url : '';
    const directIsSameOrigin = !!directUrl && isSameOrigin(directUrl);
    const displayUrl = directUrl || imageUrl;
    const displayName = currentAttachment?.name || 'image';

    const handleZoomIn = useCallback(() => setZoom(prev => Math.min(prev + 0.25, 3)), []);
    const handleZoomOut = useCallback(() => setZoom(prev => Math.max(prev - 0.25, 0.5)), []);
    const handleResetZoom = useCallback(() => setZoom(1), []);

    const handleImageLoad = useCallback(() => {
        const img = imageRef.current;
        loadedSrcRef.current = (img && (img.currentSrc || img.src)) || null;
        setImageLoaded(true);
        setImageError(false);
    }, []);

    const handleImageError = useCallback(() => {
        setImageLoaded(false);
        setImageError(true);
    }, []);

    const handleClose = useCallback(() => {
        setZoom(1);
        setImageLoaded(false);
        setImageError(false);
        setEditMode(false);
        setUndoStack([]);
        setCurrentAttachment(attachment);
        loadedSrcRef.current = null;
        onClose();
    }, [onClose, attachment]);

    const handleDownload = useCallback(async () => {
        if (directUrl) {
            // A credentialed cross-origin fetch is a hard CORS failure, so only a same-origin Pollux URL is fetched with the cookie; a third-party image is handed to the browser
            // as a plain navigation.
            if (!directIsSameOrigin) {
                window.open(directUrl, '_blank', 'noopener,noreferrer');
                return;
            }
            const knownType = currentAttachment?.mimeType || mimeTypeFromUrl(directUrl);
            if (knownType && !isRasterImage(knownType)) {
                window.open(directUrl, '_blank', 'noopener,noreferrer');
                return;
            }
            if (downloading) return;
            setDownloading(true);
            let objectUrl = null;
            try {
                const response = await fetch(directUrl, { credentials: 'include' });
                if (!response.ok) throw new Error(`the image request failed (${response.status})`);
                const blob = await response.blob();
                if (!isRasterImage(blob.type)) {
                    const link = document.createElement('a');
                    link.href = directUrl;
                    link.download = displayName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = displayName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (err) {
                dispatch(showAlert({ severity: 'error', message: `Failed to download image: ${err.message || err}` }));
            } finally {
                if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                setDownloading(false);
            }
            return;
        }
        if (onDownload) onDownload(attachment);
        handleClose();
    }, [directUrl, directIsSameOrigin, currentAttachment, downloading, displayName, dispatch, onDownload, attachment, handleClose]);

    // Sync currentAttachment when the attachment prop changes (viewer opened on a different image)
    useEffect(() => {
        setCurrentAttachment(attachment);
    }, [attachment]);

    const activeBlobUrlRef = useRef(null);

    useEffect(() => () => {
        if (activeBlobUrlRef.current) {
            URL.revokeObjectURL(activeBlobUrlRef.current);
            activeBlobUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        setImageError(false);
        const img = imageRef.current;
        const src = img ? img.currentSrc || img.src : '';
        setImageLoaded(!!src && (loadedSrcRef.current === src || (img.complete && img.naturalWidth > 0)));
    }, [open, currentAttachment]);

    useEffect(() => {
        if (!open || !currentAttachment) {
            if (activeBlobUrlRef.current) {
                URL.revokeObjectURL(activeBlobUrlRef.current);
                activeBlobUrlRef.current = null;
            }
            setImageUrl(null);
            return undefined;
        }

        if (directUrl) {
            setImageUrl(null);
            return undefined;
        }

        let cancelled = false;
        let createdUrl = null;
        fetch(`${getApiUrl()}/attachment/${currentAttachment._id}`, { credentials: 'include' })
            .then(r => {
                if (!r.ok) throw new Error('Failed to load image');
                return r.blob();
            })
            .then(blob => {
                if (cancelled) return;
                if (!isRasterImage(blob.type)) throw new Error('Not a raster image');
                const url = URL.createObjectURL(blob);
                if (activeBlobUrlRef.current && activeBlobUrlRef.current !== url) URL.revokeObjectURL(activeBlobUrlRef.current);
                createdUrl = url;
                activeBlobUrlRef.current = url;
                setImageUrl(url);
                setImageError(false);
            })
            .catch(() => {
                if (!cancelled) setImageError(true);
            });

        return () => {
            cancelled = true;
            if (!createdUrl) return;
            URL.revokeObjectURL(createdUrl);
            if (activeBlobUrlRef.current === createdUrl) activeBlobUrlRef.current = null;
            setImageUrl(prev => (prev === createdUrl ? null : prev));
        };
    }, [open, currentAttachment, directUrl]);

    // Enter edit mode — draw the image onto the canvas as the base layer
    const handleEnterEdit = useCallback(() => {
        setEditMode(true);
        setUndoStack([]);
        // Give React a tick to render the canvas
        setTimeout(() => {
            const canvas = canvasRef.current;
            const img = imageRef.current;
            if (!canvas || !img) return;

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
        }, 0);
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditMode(false);
        setUndoStack([]);
    }, []);

    // Canvas coordinate helper (handles both mouse and touch)
    const getCanvasPos = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    }, []);

    const saveSnapshot = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setUndoStack(prev => [...prev.slice(-19), snapshot]); // keep last 20
    }, []);

    const startDrawing = useCallback((e) => {
        e.preventDefault();
        saveSnapshot();
        const pos = getCanvasPos(e);
        lastPos.current = pos;
        setIsDrawing(true);

        // Draw a dot at click position
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
        if (tool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0,0,0,1)';
        } else if (tool === 'highlight') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = color + '55'; // ~33% opacity
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = color;
        }
        ctx.fill();
        ctx.closePath();
    }, [saveSnapshot, getCanvasPos, brushSize, tool, color]);

    const draw = useCallback((e) => {
        e.preventDefault();
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const pos = getCanvasPos(e);

        ctx.beginPath();
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (tool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.strokeStyle = 'rgba(0,0,0,1)';
        } else if (tool === 'highlight') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = color + '55';
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = color;
        }

        ctx.moveTo(lastPos.current.x, lastPos.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        ctx.closePath();

        lastPos.current = pos;
    }, [isDrawing, getCanvasPos, brushSize, tool, color]);

    const stopDrawing = useCallback((e) => {
        if (e) e.preventDefault();
        setIsDrawing(false);
        lastPos.current = null;
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.globalCompositeOperation = 'source-over';
        }
    }, []);

    const handleUndo = useCallback(() => {
        if (undoStack.length === 0) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const snapshot = undoStack[undoStack.length - 1];
        ctx.putImageData(snapshot, 0, 0);
        setUndoStack(prev => prev.slice(0, -1));
    }, [undoStack]);

    const handleSaveEdit = useCallback(async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        setUploadingEdit(true);
        try {
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            const baseName = attachment?.name
                ? attachment.name.replace(/\.[^.]+$/, '')
                : 'image';
            const fileName = `edited_${baseName}.png`;

            // 1. Upload blob to temp storage
            const formData = new FormData();
            formData.append('file', blob, fileName);
            const uploadRes = await fetch('/api/attachment/upload', {
                method: 'POST',
                body: formData,
            });
            if (!uploadRes.ok) throw new Error('Upload failed');
            const { id: uploadKey, size, mimetype } = await uploadRes.json();

            // 2. Commit: move from temp → permanent, create Attachment doc under same parent
            const commitRes = await fetch('/api/attachment/commit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uploadKey,
                    filename: fileName,
                    size,
                    mimetype,
                    originalAttachmentId: attachment._id,
                }),
            });
            if (!commitRes.ok) {
                const errBody = await commitRes.json().catch(() => null);
                throw new Error(errBody?.error || errBody?.message || 'Commit failed');
            }
            const newAttachment = await commitRes.json();

            if (onReupload) onReupload(newAttachment);

            // Show the edited image immediately using the blob already in memory
            const editedUrl = URL.createObjectURL(blob);
            const replacedUrl = activeBlobUrlRef.current;
            activeBlobUrlRef.current = editedUrl;
            loadedSrcRef.current = editedUrl;
            if (replacedUrl && replacedUrl !== editedUrl) URL.revokeObjectURL(replacedUrl);
            setImageUrl(editedUrl);
            setCurrentAttachment(newAttachment);
            setImageLoaded(true);
            setZoom(1);
            setEditMode(false);
            setUndoStack([]);
        } catch (err) {
            dispatch(showAlert({ severity: 'error', message: `Failed to save edited image: ${err.message || err}` }));
        } finally {
            setUploadingEdit(false);
        }
    }, [attachment, onReupload, dispatch]);

    return (
        <DarkDialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <ResetDialogContent>
                {/* Top controls — always visible */}
                <ControlsContainer>
                    {!editMode && (
                        <>
                            <ControlButton onClick={handleZoomOut} size="small">
                                <ZoomOut />
                            </ControlButton>
                            <ControlButton onClick={handleResetZoom} size="small">
                                <ZoomLabel variant="caption">
                                    {Math.round(zoom * 100)}%
                                </ZoomLabel>
                            </ControlButton>
                            <ControlButton onClick={handleZoomIn} size="small">
                                <ZoomIn />
                            </ControlButton>
                            <Tooltip title="Download">
                                <span>
                                    <ControlButton onClick={handleDownload} size="small" disabled={downloading}>
                                        {downloading ? <WhiteCircularProgress size={20} /> : <Download />}
                                    </ControlButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {imageLoaded && !!displayUrl && !imageError && !editMode && !!attachmentId && (
                        <Tooltip title="Edit / Annotate">
                            <ControlButton onClick={handleEnterEdit} size="small">
                                <Edit />
                            </ControlButton>
                        </Tooltip>
                    )}
                    <ControlButton onClick={handleClose} size="small">
                        <Close />
                    </ControlButton>
                </ControlsContainer>

                {/* View mode */}
                {!editMode && (
                    <ImageContainer>
                        {!displayUrl && !imageError && (
                            <LoadingContainer>
                                <CircularProgress />
                                <Typography>Loading image...</Typography>
                            </LoadingContainer>
                        )}
                        {imageError && (
                            <LoadingContainer>
                                <Typography color="error">Failed to load image</Typography>
                            </LoadingContainer>
                        )}
                        {!!displayUrl && !imageLoaded && !imageError && (
                            <LoadingOverlay>
                                <CircularProgress />
                            </LoadingOverlay>
                        )}
                        {!!displayUrl && !imageError && (
                            <StyledImage
                                ref={imageRef}
                                src={displayUrl}
                                alt={displayName}
                                zoom={zoom}
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                                onClick={handleZoomIn}
                            />
                        )}
                    </ImageContainer>
                )}

                {/* Edit mode — hidden img for natural dimensions + canvas */}
                {editMode && (
                    <>
                        {/* Hidden img keeps natural dimensions accessible */}
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt=""
                            style={{ display: 'none' }}
                        />
                        <CanvasWrapper>
                            <EditCanvas
                                ref={canvasRef}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                        </CanvasWrapper>
                        <EditToolbar>
                            {/* Tools */}
                            <Tooltip title="Pen">
                                <ToolButton
                                    size="small"
                                    active={tool === 'pen' ? 1 : 0}
                                    onClick={() => setTool('pen')}
                                >
                                    <Create fontSize="small" />
                                </ToolButton>
                            </Tooltip>
                            <Tooltip title="Highlighter">
                                <ToolButton
                                    size="small"
                                    active={tool === 'highlight' ? 1 : 0}
                                    onClick={() => setTool('highlight')}
                                >
                                    <BorderColor fontSize="small" />
                                </ToolButton>
                            </Tooltip>
                            <Tooltip title="Eraser">
                                <ToolButton
                                    size="small"
                                    active={tool === 'eraser' ? 1 : 0}
                                    onClick={() => setTool('eraser')}
                                >
                                    <DeleteOutline fontSize="small" />
                                </ToolButton>
                            </Tooltip>

                            <ToolbarDivider />

                            {/* Color */}
                            <Tooltip title="Color">
                                <ColorSwatch
                                    type="color"
                                    value={color}
                                    onChange={e => setColor(e.target.value)}
                                    disabled={tool === 'eraser'}
                                />
                            </Tooltip>

                            {/* Brush size */}
                            <SizeControlGroup>
                                <SliderLabel>Size</SliderLabel>
                                <WhiteSlider
                                    size="small"
                                    min={1}
                                    max={40}
                                    value={brushSize}
                                    onChange={(_, v) => setBrushSize(v)}
                                />
                                <SliderLabel>{brushSize}px</SliderLabel>
                            </SizeControlGroup>

                            <ToolbarSpacer />

                            {/* Undo */}
                            <Tooltip title="Undo">
                                <span>
                                    <ToolButton
                                        size="small"
                                        onClick={handleUndo}
                                        disabled={undoStack.length === 0}
                                    >
                                        <Undo fontSize="small" />
                                    </ToolButton>
                                </span>
                            </Tooltip>

                            {/* Cancel */}
                            <Tooltip title="Cancel edit">
                                <ToolButton size="small" onClick={handleCancelEdit}>
                                    <Close fontSize="small" />
                                </ToolButton>
                            </Tooltip>

                            {/* Save & Upload */}
                            <Tooltip title="Save & Re-upload">
                                <span>
                                    <ToolButton
                                        size="small"
                                        onClick={handleSaveEdit}
                                        disabled={uploadingEdit}
                                    >
                                        {uploadingEdit
                                            ? <WhiteCircularProgress size={16} />
                                            : <CloudUpload fontSize="small" />
                                        }
                                    </ToolButton>
                                </span>
                            </Tooltip>
                        </EditToolbar>
                    </>
                )}
            </ResetDialogContent>
        </DarkDialog>
    );
};

export default ImageViewer;
